/**
 * UI-3d Task 4 — DncPage.
 *
 * Tenant-admin Do-Not-Call list. Composition is plan §0.C (authoritative):
 *
 *   - The list is fetched with `useAdminResource('/dnc', { responseKey:
 *     'entries' })` — the backend returns `{ entries, total, limit, offset }`.
 *   - An inline `DncCheckWidget` lets admins probe whether a number is on the
 *     list without leaving the page.
 *   - "+ New" opens `DncDrawer` (create-only — DNC entries are immutable).
 *   - "Import" opens `DncImportDialog` for bulk paste-and-add.
 *   - Per-row Delete (`actions={['delete']}`) opens a `confirmDialog` whose
 *     copy warns that removal makes the number callable again and is recorded
 *     in the compliance audit log.
 *
 * Delete-by-phone (spec §3.5): DNC entries are removed by their PHONE value,
 * NOT their id — `DELETE /dnc/:phone`. The phone is URL-encoded so the leading
 * `+` survives transit (becomes `%2B`).
 *
 * The drawer + import dialog own their own API calls and report back via
 * `onSaved` / `onImported`; the page only refetches. The page issues the
 * delete itself, wrapped in try/catch so a rejecting `api.delete` surfaces a
 * toast rather than escaping as an unhandled rejection.
 */
import { useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog'

import PageHeader from '../../components/admin/PageHeader.js'
import ResourceTable, { type ColumnDef } from '../../components/admin/ResourceTable.js'
import RequirePermission from '../../components/auth/RequirePermission.js'
import DncCheckWidget from '../../components/admin/dnc/DncCheckWidget.js'
import { DncDrawer } from '../../components/admin/dnc/DncDrawer.js'
import { DncImportDialog } from '../../components/admin/dnc/DncImportDialog.js'
import { useAdminResource } from '../../hooks/useAdminApi.js'
import { api } from '../../api/client.js'
import { useToast } from '../../components/system/ToastProvider.js'
import { formatDate } from '../../utils/formatDate.js'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DncEntry {
  id: string
  phone: string
  phoneE164: string
  reason: string | null
  source: string | null
  expiresAt: string | null
  addedAt: string
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function DncPage(): JSX.Element {
  const toast = useToast()

  const {
    items: entries,
    loading,
    refetch,
    pagination,
  } = useAdminResource<DncEntry>('/dnc', { responseKey: 'entries' })

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)

  // --- Remove flow ---------------------------------------------------------

  async function removeByPhone(entry: DncEntry): Promise<void> {
    // Delete by PHONE, not id — and URL-encode so the leading `+` survives.
    try {
      await api.delete(`/dnc/${encodeURIComponent(entry.phone)}`)
      toast.success('Removed from DNC list')
      refetch()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove from DNC list')
    }
  }

  function confirmRemove(entry: DncEntry): void {
    confirmDialog({
      message: `Remove ${entry.phoneE164} from DNC list? This phone will be callable by the dialer immediately. Compliance audit log will record the removal.`,
      header: 'Remove from DNC',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      // `removeByPhone` is itself try/catch-wrapped, so the returned promise
      // always resolves — `accept` can never leak an unhandled rejection.
      accept: () => {
        void removeByPhone(entry)
      },
    })
  }

  // --- Columns -------------------------------------------------------------

  const columns: ColumnDef<DncEntry>[] = [
    { label: 'Phone', accessor: 'phoneE164' },
    { label: 'Source', accessor: (r) => r.source ?? '—' },
    { label: 'Reason', accessor: (r) => r.reason ?? '—' },
    { label: 'Expires', accessor: (r) => (r.expiresAt ? formatDate(r.expiresAt) : 'Never') },
    { label: 'Added', accessor: (r) => formatDate(r.addedAt) },
  ]

  return (
    <RequirePermission permission="agents:manage">
      <div className="space-y-4 p-4">
        <PageHeader
          title="Do Not Call List"
          description="Numbers the dialer must never call."
          actions={
            <>
              <Button label="+ New" onClick={() => setDrawerOpen(true)} />
              <Button label="Import" outlined onClick={() => setImportOpen(true)} />
            </>
          }
        />

        <DncCheckWidget />

        <ResourceTable<DncEntry>
          columns={columns}
          rows={entries}
          loading={loading}
          // DNC entries are immutable — Delete only, no Edit.
          actions={['delete']}
          // Required by the prop type but never invoked since `actions` omits 'edit'.
          onEdit={() => {}}
          onDelete={confirmRemove}
          empty={{
            title: 'No DNC entries',
            description: 'Numbers added to the Do-Not-Call list will appear here.',
            icon: 'pi pi-ban',
          }}
          pagination={pagination}
        />

        <DncDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onSaved={() => {
            refetch()
            setDrawerOpen(false)
          }}
        />

        <DncImportDialog
          open={importOpen}
          onClose={() => setImportOpen(false)}
          onImported={refetch}
        />

        <ConfirmDialog />
      </div>
    </RequirePermission>
  )
}
